import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import useFetch from '../../api/useFetch';
import usePersistedState from '../../global/usePersistedState';
import Feedback from '../../components/special/Feedback';
import ExoAlert from '../../components/exo/ExoAlert';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import WPEToDoElementPopup from '../../components/exo/project/work-package-elements/WPEToDoElementPopup';
import TaskPool from '../../components/exo/project/task-pool/TaskPool';
import EmployeeCapacityFilterBar from './EmployeeCapacityFilterBar';
import EmployeeCapacityOverview from './EmployeeCapacityOverview';
import EmployeeCapacityCard from './EmployeeCapacityCard';
import {
  applyEntryTaskChange,
  applyTaskChange,
  buildCapacitiesQuery,
  CAPACITIES_PATH,
  capacityUser,
  emptyCapacityFilter,
  PICKER_PROJECTS_PER_PAGE,
  TASK_PATH,
} from './capacityFilter';

// list of employees, expanding one shows the projects they are on
export const EmployeeCapacityList = () => {
  const { t } = useTranslation();
  const config = useConfig();

  // the pickers and the hide done switch survive a reload, only the search
  // starts out empty again
  const [storedFilter, setStoredFilter] = usePersistedState(
    'employeeCapacity.filter',
    emptyCapacityFilter,
  );
  const [search, setSearch] = useState('');

  const filter = useMemo(
    () => ({ ...storedFilter, search }),
    [storedFilter, search],
  );

  const setFilter = useCallback((next) => {
    const { search: nextSearch, ...rest } = next;
    setSearch(nextSearch || '');
    setStoredFilter((prev) => ({ ...prev, ...rest }));
  }, [setStoredFilter]);

  // search and both pickers are part of the request, so a change reloads
  const capacitiesQuery = useMemo(() => buildCapacitiesQuery(filter), [filter]);

  const { apiData, setApiData, isLoading: isListLoading } = useFetch(
    `${CAPACITIES_PATH}?${capacitiesQuery}`,
  );

  // the employees come with the capacities, the picker only needs the projects
  const { apiData: projectsData } = useFetch(
    `projects?status[eq]=0&perPage=${PICKER_PROJECTS_PER_PAGE}`,
  );

  // several employees may be open at once
  const [openIds, setOpenIds] = useState([]);

  // projects of every opened employee, so reopening costs no request
  const [projectsByUser, setProjectsByUser] = useState({});

  // the entries carry the employee and their open counters, the overview needs both
  const entries = useMemo(() => apiData?.data || [], [apiData]);

  const employees = useMemo(
    () => entries.map(capacityUser).filter(Boolean),
    [entries],
  );

  // every part of the filter narrows the answer, so the picker keeps the
  // employees of the last unfiltered one instead of offering only the hits
  const isUnfiltered =
    !filter.search && !filter.userIds.length && !filter.projectIds.length;

  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    if (isUnfiltered) setUserOptions(employees);
  }, [employees, isUnfiltered]);

  const projects = projectsData?.data || [];

  const employeeCount = apiData?.meta?.total ?? employees.length;

  // the picked projects are part of the request, so the cache is dropped
  useEffect(() => setProjectsByUser({}), [filter.projectIds]);

  const handleProjectsLoaded = useCallback(
    (userId, loaded) =>
      setProjectsByUser((prev) => ({ ...prev, [userId]: loaded })),
    [],
  );

  const [processingTaskIds, setProcessingTaskIds] = useState([]);

  // the dialogs are shared by every task, so they keep employee and project
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });

  // DBRequest reports the API message, which is empty on some endpoints
  function handleFeedback(text, type, fallbackText) {
    setAlertState({
      alertOpen: true,
      alertType: type,
      alertText: text || fallbackText,
    });
  }

  // writes a changed task back into the cached projects of its employee
  function applyTask(userId, projectId, taskId, task) {
    setProjectsByUser((prev) =>
      prev[userId]
        ? {
            ...prev,
            [userId]: applyTaskChange(prev[userId], projectId, taskId, task),
          }
        : prev,
    );
  }

  // the overview reads the counters of the employees, so they follow along
  function applyOverviewDelta(userId, before, after) {
    setApiData((prev) => applyEntryTaskChange(prev, userId, before, after));
  }

  function handleTaskToggle(userId, projectId, task) {
    if (processingTaskIds.includes(task.id)) return;

    DBRequest({
      config,
      path: `${TASK_PATH}/${task.id}`,
      method: API_PATCH({ status: task.status ? 0 : 1 }),
      onLoading: (isProcessing) =>
        setProcessingTaskIds((prev) =>
          isProcessing
            ? [...prev, task.id]
            : prev.filter((id) => id !== task.id),
        ),
      onResponse: (res) => {
        const updated = { ...task, ...res.data };
        applyTask(userId, projectId, task.id, updated);
        applyOverviewDelta(userId, task, updated);
      },
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  function handleTaskDelete() {
    const { userId, projectId, task } = taskToDelete;

    DBRequest({
      config,
      path: `${TASK_PATH}/${task.id}`,
      method: API_DELETE(),
      onResponse: () => {
        applyTask(userId, projectId, task.id, null);
        applyOverviewDelta(userId, task, null);
        setTaskToDelete(null);
      },
      onFeedback: (text, type) => handleFeedback(text, type, t('Task deleted')),
    });
  }

  // assigning someone else can take the task out of this employee, so their
  // projects are loaded again instead of guessed at
  function handleTaskUsersChange(userId) {
    setProjectsByUser((prev) => {
      const { [userId]: removed, ...rest } = prev;
      return rest;
    });
  }

  // an assignment made in the pool changes what the employees on the left
  // have on their plate: their counters follow the task and their cached
  // projects are dropped so an open card reloads
  function handlePoolUsersChange(task, members) {
    const beforeIds = (task.users || []).map((user) => user.id);
    const afterIds = members.map((user) => user.id);

    const added = afterIds.filter((id) => !beforeIds.includes(id));
    const removed = beforeIds.filter((id) => !afterIds.includes(id));

    added.forEach((userId) => {
      handleTaskUsersChange(userId);
      applyOverviewDelta(userId, null, task);
    });
    removed.forEach((userId) => {
      handleTaskUsersChange(userId);
      applyOverviewDelta(userId, task, null);
    });
  }

  // files, links and comments leave the counters alone, so they only replace
  // the task they belong to
  function patchTask(userId, projectId, task, body) {
    DBRequest({
      config,
      path: `${TASK_PATH}/${task.id}`,
      method: API_PATCH(body),
      onResponse: (res) =>
        applyTask(userId, projectId, task.id, { ...task, ...res.data }),
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  // the explorer returns the complete file selection of the task
  function handleTaskFilesChange(userId, projectId, task, files) {
    patchTask(userId, projectId, task, { ...task, files });
  }

  function handleTaskLinkAdd(userId, projectId, task, link) {
    patchTask(userId, projectId, task, {
      externalResources: [...(task.externalResources || []), link],
    });
  }

  // one link is removed by sending the remaining ones
  function handleTaskLinkDelete(userId, projectId, task, resource) {
    patchTask(userId, projectId, task, {
      ...task,
      externalResources: (task.externalResources || []).filter(
        (item) => item.id !== resource.id,
      ),
    });
  }

  // a comment becomes an entry of the task history
  function handleTaskCommentSubmit(userId, projectId, task, text) {
    DBRequest({
      config,
      path: `tasks/${task.id}/history`,
      method: API_POST({ text }),
      onResponse: (res) =>
        applyTask(userId, projectId, task.id, {
          ...task,
          history: [res.data, ...(task.history || [])],
        }),
      onFeedback: (message, type) =>
        handleFeedback(message, type, t('Comment added')),
    });
  }

  function toggleEmployee(userId) {
    setOpenIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  }

  return (
    // flex-1: the header is a sibling, so the list takes what is left
    <Box className="flex flex-col gap-3 flex-1 min-h-0 overflow-hidden pt-2">
      <EmployeeCapacityOverview entries={entries} />

      <EmployeeCapacityFilterBar
        users={userOptions}
        projects={projects}
        filter={filter}
        onChange={setFilter}
        employeeCount={employeeCount}
      />

      {/* below lg the pool moves under the employees instead of next to them */}
      <Box className="flex flex-col lg:flex-row gap-3 flex-1 min-h-0 lg:overflow-hidden overflow-y-auto">
        {/* min-h-0 turns the overflow into a scrollbar instead of squeezing the cards */}
        <Box className="flex flex-col gap-2 shrink-0 lg:flex-1 lg:min-h-0 lg:overflow-y-auto min-w-0 pr-1">
          {isListLoading && !apiData ? (
            <ExoSkeleton n={5} size="70px" />
          ) : entries.length ? (
            entries.map((entry) => {
              const user = capacityUser(entry);

              return (
                <EmployeeCapacityCard
                  key={user.id}
                  user={user}
                  entry={entry}
                  projects={projectsByUser[user.id]}
                  isOpen={openIds.includes(user.id)}
                  onToggle={() => toggleEmployee(user.id)}
                  onProjectsLoaded={handleProjectsLoaded}
                  projectIds={filter.projectIds}
                  hideDone={filter.hideDone}
                  processingTaskIds={processingTaskIds}
                  onTaskToggle={handleTaskToggle}
                  onTaskEdit={(userId, projectId, task) =>
                    setTaskToEdit({ userId, projectId, task })
                  }
                  onTaskDelete={(userId, projectId, task) =>
                    setTaskToDelete({ userId, projectId, task })
                  }
                  onTaskUsersChange={handleTaskUsersChange}
                  onTaskFilesChange={handleTaskFilesChange}
                  onTaskLinkAdd={handleTaskLinkAdd}
                  onTaskLinkDelete={handleTaskLinkDelete}
                  onTaskCommentSubmit={handleTaskCommentSubmit}
                />
              );
            })
          ) : (
            <Typography className="opacity-50 py-8 text-center">
              {t('No employees found')}
            </Typography>
          )}
        </Box>

        {/* the whole company, so no user scope - members are assigned here */}
        <Box
          className="flex shrink-0 lg:min-h-0"
          sx={{ width: { xs: '100%', lg: '400px', xl: '440px' } }}
        >
          <TaskPool
            manage
            users={userOptions}
            projects={projects}
            onUsersChange={handlePoolUsersChange}
            onFeedback={handleFeedback}
          />
        </Box>
      </Box>

      {/* the same dialog the work package view edits a task with */}
      {taskToEdit && (
        <WPEToDoElementPopup
          open={Boolean(taskToEdit)}
          element={taskToEdit.task}
          pid={taskToEdit.projectId}
          wpeId={taskToEdit.task.workPackageListElement?.id}
          onClose={() => setTaskToEdit(null)}
          onResponse={(res) => {
            const updated = { ...taskToEdit.task, ...(res?.data || res) };

            applyTask(
              taskToEdit.userId,
              taskToEdit.projectId,
              taskToEdit.task.id,
              updated,
            );
            // editing can change the status and the points of a task
            applyOverviewDelta(taskToEdit.userId, taskToEdit.task, updated);
            setTaskToEdit(null);
          }}
          onDelete={() => {
            setTaskToDelete(taskToEdit);
            setTaskToEdit(null);
          }}
        />
      )}

      <ExoAlert
        open={Boolean(taskToDelete)}
        onClose={() => setTaskToDelete(null)}
        onSubmit={handleTaskDelete}
        alert={{
          submitLabel: t('Delete'),
          submitColor: 'error',
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />

      {/* the cards are backdrop-blurred, which would pin the fixed snackbar to
          them instead of the viewport */}
      {createPortal(
        <Feedback state={alertState} setState={setAlertState} />,
        document.body,
      )}
    </Box>
  );
};

export default EmployeeCapacityList;
